import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { authApi } from '../lib/api';

const RESEND_COOLDOWN_S = 60;

const CALLBACK_ERRORS: Record<string, string> = {
  auth_failed:   'Authentication failed. Please try again.',
  missing_code:  'Invalid callback. Please start the login flow again.',
};

// ── Shared styles ─────────────────────────────────────────────
const inputStyle: React.CSSProperties = {
  width: '100%',
  backgroundColor: '#ffffff',
  border: '1px solid #e2e8f0',
  color: '#0f172a',
  padding: '0.75rem 1rem',
  fontFamily: "'Roboto', system-ui, sans-serif",
  fontSize: '0.9rem',
  fontWeight: 300,
  borderRadius: '8px',
  outline: 'none',
  transition: 'border-color 0.15s, box-shadow 0.15s',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.72rem',
  fontWeight: 500,
  color: '#64748b',
  marginBottom: '0.4rem',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

function PrimaryButton({
  children,
  disabled,
  onClick,
  type = 'submit',
}: {
  children: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  type?: 'submit' | 'button';
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: '100%',
        fontFamily: "'Roboto', system-ui, sans-serif",
        fontSize: '0.9rem',
        fontWeight: 400,
        padding: '0.85rem 2rem',
        borderRadius: '8px',
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        backgroundColor: disabled ? '#7dd3fc' : hovered ? '#0284c7' : '#0ea5e9',
        color: '#ffffff',
        transform: hovered && !disabled ? 'translateY(-1px)' : 'none',
        boxShadow: hovered && !disabled
          ? '0 4px 12px rgba(14,165,233,0.35)'
          : '0 1px 3px rgba(14,165,233,0.2)',
        transition: 'transform 0.15s, box-shadow 0.15s, background-color 0.15s',
        opacity: disabled ? 0.7 : 1,
      }}
    >
      {children}
    </button>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div
      className="mb-4 px-4 py-3 rounded-lg text-sm"
      style={{
        backgroundColor: '#fee2e2',
        border: '1px solid rgba(239,68,68,0.3)',
        color: '#991b1b',
        fontWeight: 400,
      }}
    >
      {message}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────

export default function LoginPage() {
  const [searchParams]                      = useSearchParams();

  const [step, setStep]                     = useState<'email' | 'otp'>('email');
  const [visible, setVisible]               = useState(true);

  const [email, setEmail]                   = useState('');
  const [emailLoading, setEmailLoading]     = useState(false);
  const [emailError, setEmailError]         = useState<string | null>(null);

  const [digits, setDigits]                 = useState(['', '', '', '', '', '']);
  const [otpLoading, setOtpLoading]         = useState(false);
  const [otpError, setOtpError]             = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  const digitRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    const code = searchParams.get('error');
    if (code) setEmailError(CALLBACK_ERRORS[code] ?? 'Something went wrong. Please try again.');
  }, [searchParams]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  useEffect(() => {
    if (step === 'otp' && visible) {
      setTimeout(() => digitRefs.current[0]?.focus(), 50);
    }
  }, [step, visible]);

  const transitionTo = (next: 'email' | 'otp') => {
    setVisible(false);
    setTimeout(() => {
      setStep(next);
      setVisible(true);
    }, 180);
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailLoading(true);
    setEmailError(null);

    try {
      await authApi.sendOtp(email);

      setDigits(['', '', '', '', '', '']);
      setOtpError(null);
      setResendCooldown(RESEND_COOLDOWN_S);
      transitionTo('otp');
    } catch (err) {
      setEmailError(err instanceof Error ? err.message : 'Could not send code. Please try again.');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleDigitChange = (value: string, index: number) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    if (digit && index < 5) {
      digitRefs.current[index + 1]?.focus();
    }
  };

  const handleDigitKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      digitRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0)  digitRefs.current[index - 1]?.focus();
    if (e.key === 'ArrowRight' && index < 5) digitRefs.current[index + 1]?.focus();
  };

  const handleDigitPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = [...digits];
    for (let i = 0; i < 6; i++) next[i] = pasted[i] ?? '';
    setDigits(next);
    const lastFilled = Math.min(pasted.length, 5);
    digitRefs.current[lastFilled]?.focus();
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = digits.join('');
    if (code.length < 6) {
      setOtpError('Please enter all 6 digits.');
      return;
    }

    setOtpLoading(true);
    setOtpError(null);

    try {
      await authApi.verifyOtp(email, code);
      window.location.href = '/dashboard';
    } catch (err) {
      setOtpError(err instanceof Error ? err.message : 'Invalid code. Please try again.');
      setOtpLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setOtpError(null);
    setDigits(['', '', '', '', '', '']);

    try {
      await authApi.sendOtp(email);
      setResendCooldown(RESEND_COOLDOWN_S);
      setTimeout(() => digitRefs.current[0]?.focus(), 50);
    } catch (err) {
      setOtpError(err instanceof Error ? err.message : 'Could not resend. Please try again.');
    }
  };

  const handleBack = () => {
    setOtpError(null);
    transitionTo('email');
  };

  return (
    <div
      className="min-h-screen dot-grid flex flex-col items-center justify-center px-4"
      style={{ fontFamily: "'Roboto', system-ui, sans-serif" }}
    >
      <div className="w-full max-w-sm">
        <div
          className="card-glow rounded-2xl px-8 pt-8 pb-6"
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e2e8f0',
          }}
        >
          {/* Logo */}
          <div className="mb-1">
            <span style={{ fontSize: '1.75rem', fontWeight: 300, letterSpacing: '-0.03em', lineHeight: 1 }}>
              <span style={{ color: '#0f172a' }}>Spend</span>
              <span style={{ color: '#0ea5e9' }}>Lens</span>
            </span>
          </div>

          <p className="text-sm mb-5" style={{ color: '#64748b', fontWeight: 300, lineHeight: 1.6 }}>
            See exactly what you should be paying.
          </p>

          <div className="mb-6" style={{ borderTop: '1px solid #e2e8f0' }} />

          {/* Animated content area */}
          <div style={{ transition: 'opacity 0.18s ease', opacity: visible ? 1 : 0 }}>
            {step === 'email' ? (
              <EmailStep
                email={email}
                setEmail={setEmail}
                loading={emailLoading}
                error={emailError}
                onSubmit={handleSendOtp}
              />
            ) : (
              <OtpStep
                email={email}
                digits={digits}
                digitRefs={digitRefs}
                loading={otpLoading}
                error={otpError}
                resendCooldown={resendCooldown}
                onDigitChange={handleDigitChange}
                onDigitKeyDown={handleDigitKeyDown}
                onDigitPaste={handleDigitPaste}
                onSubmit={handleVerifyOtp}
                onResend={handleResend}
                onBack={handleBack}
              />
            )}
          </div>

          {/* Footer */}
          <div className="mt-6 pt-4" style={{ borderTop: '1px solid #e2e8f0' }}>
            <p className="text-center" style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 300 }}>
              © 2026 Cyberwerk Ventures
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Email step ────────────────────────────────────────────────

function EmailStep({
  email, setEmail, loading, error, onSubmit,
}: {
  email: string;
  setEmail: (v: string) => void;
  loading: boolean;
  error: string | null;
  onSubmit: (e: React.FormEvent) => void;
}) {
  const [focused, setFocused] = useState(false);

  return (
    <form onSubmit={onSubmit}>
      <div className="mb-3">
        <label htmlFor="email" style={labelStyle}>Work Email</label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          autoFocus
          required
          disabled={loading}
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="you@yourcompany.com"
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            ...inputStyle,
            opacity: loading ? 0.6 : 1,
            borderColor: error ? '#ef4444' : focused ? '#0ea5e9' : '#e2e8f0',
            boxShadow: focused ? '0 0 0 3px rgba(14,165,233,0.15)' : 'none',
          }}
        />
      </div>

      {error && <ErrorBanner message={error} />}

      <PrimaryButton disabled={loading}>
        {loading ? 'Sending code…' : 'Continue →'}
      </PrimaryButton>

      <p className="text-center mt-3" style={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: 300 }}>
        We'll send a 6-digit code to your email
      </p>
    </form>
  );
}

// ── OTP step ─────────────────────────────────────────────────

function OtpStep({
  email, digits, digitRefs, loading, error, resendCooldown,
  onDigitChange, onDigitKeyDown, onDigitPaste, onSubmit, onResend, onBack,
}: {
  email: string;
  digits: string[];
  digitRefs: React.MutableRefObject<(HTMLInputElement | null)[]>;
  loading: boolean;
  error: string | null;
  resendCooldown: number;
  onDigitChange: (value: string, index: number) => void;
  onDigitKeyDown: (e: React.KeyboardEvent<HTMLInputElement>, index: number) => void;
  onDigitPaste: (e: React.ClipboardEvent) => void;
  onSubmit: (e: React.FormEvent) => void;
  onResend: () => void;
  onBack: () => void;
}) {
  const allFilled = digits.every(d => d !== '');

  return (
    <form onSubmit={onSubmit}>
      <button
        type="button"
        onClick={onBack}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: '#94a3b8',
          fontSize: '0.82rem',
          fontFamily: "'Roboto', system-ui, sans-serif",
          padding: '0 0 0.75rem 0',
          display: 'flex',
          alignItems: 'center',
          gap: '0.3rem',
        }}
      >
        ← Back
      </button>

      <div style={{ marginBottom: '0.35rem' }}>
        <span style={{ fontSize: '1.05rem', fontWeight: 400, color: '#0f172a' }}>
          Check your email
        </span>
      </div>
      <p style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 300, marginBottom: '1.5rem', lineHeight: 1.5 }}>
        We sent a 6-digit code to{' '}
        <span style={{ color: '#0f172a', fontWeight: 400 }}>{email}</span>
      </p>

      {error && <ErrorBanner message={error} />}

      <div style={{ display: 'flex', gap: '8px', marginBottom: '1.25rem' }}>
        {digits.map((digit, i) => (
          <OtpDigit
            key={i}
            index={i}
            value={digit}
            disabled={loading}
            digitRefs={digitRefs}
            onChange={onDigitChange}
            onKeyDown={onDigitKeyDown}
            onPaste={i === 0 ? onDigitPaste : undefined}
          />
        ))}
      </div>

      <PrimaryButton disabled={loading || !allFilled}>
        {loading ? 'Verifying…' : 'Verify →'}
      </PrimaryButton>

      <div className="text-center mt-3" style={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: 300 }}>
        {resendCooldown > 0 ? (
          <span>Resend code in {resendCooldown}s</span>
        ) : (
          <button
            type="button"
            onClick={onResend}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#0ea5e9',
              fontSize: '0.78rem',
              fontFamily: "'Roboto', system-ui, sans-serif",
              padding: 0,
            }}
          >
            Resend code
          </button>
        )}
      </div>
    </form>
  );
}

// ── Single OTP digit input ────────────────────────────────────

function OtpDigit({
  index, value, disabled, digitRefs, onChange, onKeyDown, onPaste,
}: {
  index: number;
  value: string;
  disabled: boolean;
  digitRefs: React.MutableRefObject<(HTMLInputElement | null)[]>;
  onChange: (value: string, index: number) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>, index: number) => void;
  onPaste?: (e: React.ClipboardEvent) => void;
}) {
  const [focused, setFocused] = useState(false);

  return (
    <input
      ref={el => { digitRefs.current[index] = el; }}
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      maxLength={1}
      value={value}
      disabled={disabled}
      onChange={e => onChange(e.target.value, index)}
      onKeyDown={e => onKeyDown(e, index)}
      onPaste={onPaste}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        flex: 1,
        minWidth: 0,
        height: '56px',
        backgroundColor: '#ffffff',
        border: `1px solid ${focused ? '#0ea5e9' : value ? 'rgba(14,165,233,0.5)' : '#e2e8f0'}`,
        boxShadow: focused ? '0 0 0 3px rgba(14,165,233,0.15)' : 'none',
        borderRadius: '8px',
        color: '#0f172a',
        fontSize: '1.4rem',
        fontWeight: 400,
        textAlign: 'center',
        outline: 'none',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        opacity: disabled ? 0.7 : 1,
        caretColor: 'transparent',
        fontFamily: "'Roboto', system-ui, sans-serif",
      }}
    />
  );
}
